import { useState, useRef } from 'react'
import { Edit3 } from 'lucide-react'
import { Label } from '../ui/label'
import { Badge } from '../ui/badge'
import { CategorySelector } from './CategorySelector'
import { useClickOutside } from '../../hooks/useClickOutside'
import { labelStyle, badgeStyle, valueStyle } from '../../styles/typography'

interface InlineEditCategoryProps {
  selectedCategoryId: string | null
  selectedSubcategoryId: string | null
  onCategoryChange: (categoryId: string | null) => void
  onSubcategoryChange: (subcategoryId: string | null) => void
  categoryName?: string
  subcategoryName?: string
  categoryError?: boolean
  className?: string
}

export function InlineEditCategory({
  selectedCategoryId,
  selectedSubcategoryId,
  onCategoryChange,
  onSubcategoryChange,
  categoryName,
  subcategoryName,
  categoryError = false,
  className = ''
}: InlineEditCategoryProps) {
  const [isEditing, setIsEditing] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)

  useClickOutside(wrapperRef, () => setIsEditing(false), isEditing)

  if (isEditing) {
    return (
      <div className={className} ref={wrapperRef}>
        <CategorySelector
          selectedCategoryId={selectedCategoryId}
          selectedSubcategoryId={selectedSubcategoryId}
          onCategoryChange={onCategoryChange}
          onSubcategoryChange={onSubcategoryChange}
          categoryError={categoryError}
          isEditable={true}
        />
      </div>
    )
  }

  // Display mode (click to edit)
  return (
    <div
      className={`group cursor-pointer ${className}`}
      onClick={() => setIsEditing(true)}
    >
      <div className="grid grid-cols-2 gap-x-8">
        {/* Category */}
        <div>
          <Label style={labelStyle}>
            Category <span className="text-red-500">*</span>
          </Label>
          <div className="flex items-center gap-2 mt-1">
            {categoryName ? (
              <Badge variant="secondary" style={badgeStyle}>
                {categoryName}
              </Badge>
            ) : (
              <span
                className={`${categoryError ? 'text-red-500' : 'text-gray-400 italic'}`}
                style={valueStyle}
              >
                Click to select...
              </span>
            )}
            <Edit3 className="h-3.5 w-3.5 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
          </div>
        </div>

        {/* Subcategory */}
        <div>
          <Label style={labelStyle}>
            Subcategory
          </Label>
          <div className="flex items-center gap-2 mt-1">
            {subcategoryName ? (
              <Badge variant="outline" style={badgeStyle}>
                {subcategoryName}
              </Badge>
            ) : (
              <span className="text-gray-400" style={valueStyle}>
                —
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
